// src/pages/sqf/FoodSafetyWizardPage.js
// Guided, plain-language walkthrough for a first-time client to build a full USDA/FDA Food
// Safety Plan. Pure orchestration on top of the existing plan/product/AI-generate APIs — see
// docs/FOOD_SAFETY_WIZARD_MODULE.md. No new backend endpoints.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import foodSafetyService from '../../services/foodSafetyService';
import productService from '../../services/productService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

// Plain-language explainer per section key — written for someone who has never heard the term
// "preventive control" before. Not a regulatory citation dump.
const SECTION_HELP = {
  scope_facility: 'Describe your facility and what you make here — this sets the stage for the rest of the plan.',
  management_commitment: "A short statement that leadership supports and resources this food safety plan. Doesn't need to be long.",
  gmp_prerequisite: 'Your basic hygiene and cleanliness rules — handwashing, clean uniforms, no jewelry, etc. — that apply every day regardless of product.',
  ssop_general: 'How and when you clean and sanitize equipment and surfaces, and who checks that it was done.',
  allergen_control: 'How you keep allergens (like milk, wheat, nuts) from accidentally ending up in a product that shouldn’t have them, and how you label correctly.',
  supplier_control: 'How you make sure the ingredients you buy are safe before they even arrive — approving suppliers, checking certificates.',
  food_defense: 'How you protect your food from intentional tampering (not accidental contamination) — building security, visitor controls.',
  recall_plan: 'The step-by-step plan for pulling a product back if something goes wrong after it ships.',
  pest_control: 'How you keep insects and rodents out of the facility, and what you do if you find signs of them.',
  employee_training: 'What food safety training your employees get, and how often.',
  equipment_list: 'A list of the equipment used in production — what it’s made of, where it is, and how often it’s cleaned/maintained.',
  water_quality: 'Where your water comes from and how you confirm it’s safe to use in food.',
  waste_management: 'How you handle trash and food waste so it doesn’t attract pests or contaminate product.',
  environmental_monitoring: 'If you test surfaces/air/drains for contamination (like Listeria) even when nothing looks wrong.',
  fda_product_descriptions: 'A description of each product and how it’s meant to be used — this is built automatically from the products you add in the next step.',
  fda_flow_diagrams: 'A diagram showing every step your product goes through, from receiving ingredients to shipping. You’ll need to add this as an image/upload later in the full editor.',
  fda_hazard_analysis: 'For each step in your process, think through what could realistically go wrong — could something biological, chemical, or physical make this food unsafe? List every real risk, even ones you already control for.',
  fda_preventive_controls: 'For each real hazard you identified, what specifically do you do to control it? (A process step, a temperature, a check.)',
  fda_process_controls: 'Preventive controls specifically tied to how you process the food (cooking, cooling, etc.).',
  fda_allergen_controls_pc: 'Preventive controls specifically for preventing allergen cross-contact.',
  fda_sanitation_controls: 'Preventive controls specifically for cleaning/sanitation that prevent contamination.',
  fda_supply_chain_controls: 'Preventive controls you rely on your suppliers to handle before the ingredient reaches you.',
  fda_allergen_matrix: 'A grid showing which allergens are in which product — built automatically from your product list.',
  fda_pcqi_designation: 'Who is your Preventive Controls Qualified Individual (PCQI) — the person certified to oversee this plan.',
  fda_monitoring_records: 'What records you keep to prove your controls are actually being followed day to day.',
  fda_corrective_actions: 'What you do when a control fails — how you fix the immediate problem and the product affected.',
  fda_verification: 'How you double-check that your controls are actually working over time (not just being followed).',
  fda_reanalysis: 'How often you revisit and update this whole plan (at least every 3 years, or when something changes).',
  fda_modifications_record: 'An automatic log of every change made to this plan over time — required by FDA, built for you.',
  usda_product_descriptions: 'A description of each product and how it’s meant to be used — built automatically from the products you add in the next step.',
  usda_flow_diagrams: 'A diagram showing every step your product goes through, from receiving to shipping. Add this as an image/upload later in the full editor.',
  usda_hazard_analysis: 'For each step/ingredient, think through what could realistically make this food unsafe, and whether that step needs to be a Critical Control Point (CCP).',
  usda_haccp_plan_table: 'For each Critical Control Point you identified, what’s the exact limit (e.g. a temperature), how do you monitor it, and what do you do if it’s exceeded.',
  usda_prerequisite_programs: 'The basic programs (sanitation, pest control, allergen control, etc.) that support your HACCP plan.',
  usda_allergen_matrix: 'A grid showing which allergens are in which product — built automatically from your product list.',
  usda_haccp_coordinator: 'Who leads your HACCP team and is responsible for keeping this plan accurate.',
  usda_monitoring_records: 'What forms/logs you use to record your CCP monitoring.',
  usda_corrective_actions: 'What you do when a Critical Control Point is out of limit — how you fix it and handle the affected product.',
  usda_verification: 'How often, and how, you confirm the whole HACCP plan is still working as designed (at least annually).',
  usda_record_keeping: 'How long you keep your HACCP records and how they’re organized.',
  usda_modifications_record: 'An automatic log of every change made to this plan over time — built for you.',
};

const PLAN_LABEL = { fda: 'FDA Food Safety Plan', usda: 'USDA HACCP Plan' };

function StepDot({ n, step }) {
  return (
    <div
      className={`d-flex align-items-center justify-content-center rounded-circle border ${step >= n ? 'bg-primary border-primary text-white' : 'border-secondary text-muted'}`}
      style={{ width: 28, height: 28, fontSize: 13, fontWeight: 700, flexShrink: 0 }}
    >
      {step > n ? '✓' : n}
    </div>
  );
}

export default function FoodSafetyWizardPage() {
  const { lang } = useLanguage();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('loading'); // loading | plans | facility | products | sections | finish
  const [error, setError] = useState('');

  // Step: which plan(s)
  const [existingPlanTypes, setExistingPlanTypes] = useState([]); // plan types that already exist
  const [wantFda, setWantFda] = useState(false);
  const [wantUsda, setWantUsda] = useState(false);
  const [creatingPlans, setCreatingPlans] = useState(false);

  // Walk order across both plans, built once plans exist
  const [planQueue, setPlanQueue] = useState([]); // e.g. ['fda','usda']
  const [planIdx, setPlanIdx] = useState(0);
  const activePlanType = planQueue[planIdx] || null;

  // Facility blurb (feeds scope_facility, once overall)
  const [facilityNotes, setFacilityNotes] = useState('');
  const [facilitySaving, setFacilitySaving] = useState(false);
  const [facilityAlreadyDone, setFacilityAlreadyDone] = useState(false);

  // Products
  const [allProducts, setAllProducts] = useState([]);
  const [assignedProductIds, setAssignedProductIds] = useState(new Set());
  const [newProduct, setNewProduct] = useState(null); // { productId, name, intendedUse, ingredients, allergens, storageConds }
  const [productSaving, setProductSaving] = useState(false);
  const [checkingRules, setCheckingRules] = useState(false);
  const [ruleResults, setRuleResults] = useState(null);

  // Section walkthrough
  const [sectionsToWalk, setSectionsToWalk] = useState([]); // for activePlanType
  const [sectionIdx, setSectionIdx] = useState(0);
  const [sectionDraft, setSectionDraft] = useState('');
  const [sectionGenerating, setSectionGenerating] = useState(false);
  const [sectionProgress, setSectionProgress] = useState('');
  const [sectionSaving, setSectionSaving] = useState(false);
  const [sectionDone, setSectionDone] = useState({}); // key -> 'generated' | 'skipped'

  const activeSection = sectionsToWalk[sectionIdx] || null;

  // ─── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    foodSafetyService.getPlans()
      .then(plans => {
        const types = (plans || []).map(p => p.planType);
        setExistingPlanTypes(types);
        setWantFda(types.includes('fda'));
        setWantUsda(types.includes('usda'));
        setPhase('plans');
      })
      .catch(() => setError(t('Failed to load existing plans.', 'Error al cargar los planes existentes.', lang)))
      .finally(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Step 1: plan selection ────────────────────────────────────────────────
  const handleCreatePlans = async () => {
    if (!wantFda && !wantUsda) { setError(t('Select at least one.', 'Selecciona al menos uno.', lang)); return; }
    setCreatingPlans(true); setError('');
    try {
      const queue = [];
      if (wantFda) { queue.push('fda'); if (!existingPlanTypes.includes('fda')) await foodSafetyService.createPlan({ planType: 'fda', name: PLAN_LABEL.fda }); }
      if (wantUsda) { queue.push('usda'); if (!existingPlanTypes.includes('usda')) await foodSafetyService.createPlan({ planType: 'usda', name: PLAN_LABEL.usda }); }
      setPlanQueue(queue);
      setPlanIdx(0);

      // Has scope_facility already been filled (e.g. from a prior session)?
      const plan = await foodSafetyService.getPlan(queue[0]);
      const scopeEntry = (plan.orderedSections || []).find(s => s.key === 'scope_facility');
      const alreadyFilled = !!scopeEntry?.section?.content?.trim();
      setFacilityAlreadyDone(alreadyFilled);
      setPhase('facility');
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to create plan.', 'Error al crear el plan.', lang));
    } finally {
      setCreatingPlans(false);
    }
  };

  // ─── Step 2: facility blurb → scope_facility section ──────────────────────
  const handleGenerateFacility = async () => {
    if (!facilityNotes.trim()) { setError(t('Tell us a little about your facility first.', 'Cuéntanos un poco sobre tu instalación primero.', lang)); return; }
    setFacilitySaving(true); setError('');
    try {
      const content = await foodSafetyService.generateSection(activePlanType, 'scope_facility', { notes: facilityNotes, lang }, () => {});
      await foodSafetyService.saveSection(activePlanType, 'scope_facility', content);
      await loadProductsStep();
    } catch (err) {
      setError(err?.message || t('Failed to generate this section.', 'Error al generar esta sección.', lang));
    } finally {
      setFacilitySaving(false);
    }
  };

  // ─── Step 3: products ──────────────────────────────────────────────────────
  const loadProductsStep = useCallback(async () => {
    setError('');
    try {
      const [prods, plan] = await Promise.all([
        productService.getProducts().then(r => r.products || r || []),
        foodSafetyService.getPlan(activePlanType || planQueue[0]),
      ]);
      setAllProducts(prods);
      setAssignedProductIds(new Set((plan.plan?.PlanProducts || []).map(p => p.productId)));
      setPhase('products');
    } catch {
      setError(t('Failed to load products.', 'Error al cargar los productos.', lang));
    }
  }, [activePlanType, planQueue, lang]);

  const startAddProduct = (productId) => {
    const p = allProducts.find(x => x.id === Number(productId));
    if (!p) return;
    setNewProduct({ productId: p.id, name: p.name, intendedUse: '', ingredients: '', allergens: '', storageConds: '', firstPlanPpId: null });
    setRuleResults(null);
  };

  // Gets (creating if needed) the FoodSafetyPlanProduct row for the first selected plan type —
  // shared by "Check Rules" and "Save" so we never try to create the same (planId, productId)
  // pair twice, which the backend rejects with a 409.
  const ensureFirstPlanPp = async () => {
    if (newProduct.firstPlanPpId) return newProduct.firstPlanPpId;
    const pp = await foodSafetyService.addProduct(planQueue[0], { productId: newProduct.productId });
    setNewProduct(p => (p ? { ...p, firstPlanPpId: pp.id } : p));
    return pp.id;
  };

  const handleSaveProduct = async () => {
    if (!newProduct?.productId) return;
    if (!newProduct.ingredients.trim()) { setError(t('Ingredients are required — they drive the AI drafts and allergen matrix.', 'Los ingredientes son obligatorios — impulsan los borradores de IA y la matriz de alérgenos.', lang)); return; }
    setProductSaving(true); setError('');
    try {
      const fields = {
        intendedUse: newProduct.intendedUse,
        ingredients: newProduct.ingredients,
        allergens: newProduct.allergens,
        storageConds: newProduct.storageConds,
      };
      // Assign to every selected plan type, not just the one currently active, so the user never
      // has to repeat this step for the second plan. The first plan type may already have a row
      // from "Check Rules" — reuse it instead of creating a duplicate.
      const firstPpId = await ensureFirstPlanPp();
      await foodSafetyService.updateProduct(planQueue[0], firstPpId, fields);
      for (const pt of planQueue.slice(1)) {
        const planProduct = await foodSafetyService.addProduct(pt, { productId: newProduct.productId });
        await foodSafetyService.updateProduct(pt, planProduct.id, fields);
      }
      setAssignedProductIds(prev => new Set([...prev, newProduct.productId]));
      setNewProduct(null);
      setRuleResults(null);
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to save product.', 'Error al guardar el producto.', lang));
    } finally {
      setProductSaving(false);
    }
  };

  const handleCheckRules = async () => {
    if (!newProduct) return;
    setCheckingRules(true); setError('');
    try {
      const ppId = await ensureFirstPlanPp();
      const rules = await foodSafetyService.checkProductRules(planQueue[0], ppId, { allergens: newProduct.allergens, ingredients: newProduct.ingredients });
      setRuleResults(rules);
    } catch {
      setError(t('Failed to check rules — you can still continue without this.', 'Error al verificar las reglas — aún puedes continuar sin esto.', lang));
    } finally {
      setCheckingRules(false);
    }
  };

  const handleProductsDone = async () => {
    if (assignedProductIds.size === 0) { setError(t('Add at least one product before continuing.', 'Agrega al menos un producto antes de continuar.', lang)); return; }
    setError('');
    await loadSectionsForPlan(planQueue[0]);
  };

  // ─── Step 4: section walkthrough ───────────────────────────────────────────
  const loadSectionsForPlan = useCallback(async (planType) => {
    setError('');
    try {
      const { orderedSections } = await foodSafetyService.getPlan(planType);
      // auto_generated has nothing to do; image (flow diagrams) can't be AI-drafted — both are
      // left for the full editor, not shown in the wizard at all.
      const toWalk = (orderedSections || []).filter(s =>
        s.contentType !== 'auto_generated' &&
        s.contentType !== 'image' &&
        s.key !== 'scope_facility' &&
        !s.section?.content?.trim()
      );
      setSectionsToWalk(toWalk);
      setSectionIdx(0);
      setSectionDraft('');
      setPhase('sections');
    } catch {
      setError(t('Failed to load plan sections.', 'Error al cargar las secciones del plan.', lang));
    }
  }, [lang]);

  const handleGenerateSection = async () => {
    if (!activeSection) return;
    setSectionGenerating(true); setSectionProgress(''); setError('');
    try {
      const content = await foodSafetyService.generateSection(
        activePlanType, activeSection.key, { notes: '', lang },
        (msg) => setSectionProgress(msg)
      );
      setSectionDraft(content);
    } catch (err) {
      setError(err?.message || t('Generation failed — try again or skip for now.', 'La generación falló — intenta de nuevo o omite por ahora.', lang));
    } finally {
      setSectionGenerating(false);
    }
  };

  const advanceSection = (status) => {
    setSectionDone(prev => ({ ...prev, [activeSection.key]: status }));
    setSectionDraft('');
    setRuleResults(null);
    if (sectionIdx + 1 < sectionsToWalk.length) {
      setSectionIdx(i => i + 1);
    } else if (planIdx + 1 < planQueue.length) {
      const nextIdx = planIdx + 1;
      setPlanIdx(nextIdx);
      loadSectionsForPlan(planQueue[nextIdx]);
    } else {
      setPhase('finish');
    }
  };

  const handleSaveSection = async () => {
    if (!activeSection || !sectionDraft) return;
    setSectionSaving(true); setError('');
    try {
      await foodSafetyService.saveSection(activePlanType, activeSection.key, sectionDraft);
      advanceSection('generated');
    } catch {
      setError(t('Failed to save — try again.', 'Error al guardar — intenta de nuevo.', lang));
    } finally {
      setSectionSaving(false);
    }
  };

  const handleSkipSection = () => advanceSection('skipped');

  const handleAllSectionsAlreadyComplete = () => {
    if (planIdx + 1 < planQueue.length) {
      const nextIdx = planIdx + 1;
      setPlanIdx(nextIdx);
      loadSectionsForPlan(planQueue[nextIdx]);
    } else {
      setPhase('finish');
    }
  };

  const handleCancelProduct = async () => {
    // "Check Rules" may have created a plan-product row before Save was clicked — clean it up so
    // the product can be re-picked later without hitting the unique-constraint 409.
    if (newProduct?.firstPlanPpId) {
      await foodSafetyService.removeProduct(planQueue[0], newProduct.firstPlanPpId).catch(() => {});
    }
    setNewProduct(null);
    setRuleResults(null);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  const totalPhaseSteps = 5;
  const phaseNum = { loading: 1, plans: 1, facility: 2, products: 3, sections: 4, finish: 5 }[phase] || 1;

  if (phase === 'loading') {
    return <div className="text-center py-5"><span className="spinner-border" /></div>;
  }

  return (
    <>
      <SqfNav />
      <div className="container py-4" style={{ maxWidth: 680 }}>
        <div className="d-flex align-items-center gap-2 mb-4">
          <Link to="/sqf/food-safety" className="text-muted text-decoration-none" style={{ fontSize: 13 }}>
            ← {t('Food Safety Plans', 'Planes de Inocuidad', lang)}
          </Link>
          <span className="text-muted">/</span>
          <h5 className="mb-0">✨ {t('Guided Setup', 'Configuración Guiada', lang)}</h5>
        </div>

        <div className="d-flex align-items-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map(n => (
            <React.Fragment key={n}>
              <StepDot n={n} step={phaseNum} />
              {n < totalPhaseSteps && <span className={`flex-fill border-top ${phaseNum > n ? 'border-primary' : 'border-secondary'}`} style={{ borderWidth: 2 }} />}
            </React.Fragment>
          ))}
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-start gap-2 py-2" style={{ fontSize: 13 }}>
            <span>⚠️</span><span className="flex-fill">{error}</span>
            <button className="btn-close btn-sm" onClick={() => setError('')} />
          </div>
        )}

        {/* ── Step 1: which plan(s) ─────────────────────────────────────────── */}
        {phase === 'plans' && (
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-2">{t('Which plan(s) do you need?', '¿Qué plan(es) necesitas?', lang)}</h6>
              <p className="text-muted small mb-3">
                {t('USDA inspects meat, poultry, and egg products. FDA covers everything else — bakery, dairy, produce, packaged foods, and more. If you’re not sure, ask your co-packer or regulatory contact — many facilities only need one.',
                   'USDA inspecciona productos cárnicos, avícolas y de huevo. FDA cubre todo lo demás — panadería, lácteos, productos frescos, alimentos envasados, y más. Si no estás seguro, pregunta a tu maquilador o contacto regulatorio — muchas instalaciones solo necesitan uno.', lang)}
              </p>
              <div className="form-check mb-2">
                <input className="form-check-input" type="checkbox" id="wantFda" checked={wantFda} onChange={e => setWantFda(e.target.checked)} />
                <label className="form-check-label" htmlFor="wantFda">
                  <strong>FDA Food Safety Plan</strong> {existingPlanTypes.includes('fda') && <span className="badge bg-secondary ms-1">{t('already started', 'ya iniciado', lang)}</span>}
                </label>
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="wantUsda" checked={wantUsda} onChange={e => setWantUsda(e.target.checked)} />
                <label className="form-check-label" htmlFor="wantUsda">
                  <strong>USDA HACCP Plan</strong> {existingPlanTypes.includes('usda') && <span className="badge bg-secondary ms-1">{t('already started', 'ya iniciado', lang)}</span>}
                </label>
              </div>
              <button className="btn btn-primary" onClick={handleCreatePlans} disabled={creatingPlans}>
                {creatingPlans ? <><span className="spinner-border spinner-border-sm me-2" />{t('Setting up…', 'Configurando…', lang)}</> : t('Continue', 'Continuar', lang)}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: facility blurb ───────────────────────────────────────── */}
        {phase === 'facility' && (
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-2">{t('Tell us about your facility', 'Cuéntanos sobre tu instalación', lang)}</h6>
              {facilityAlreadyDone ? (
                <>
                  <p className="text-muted small mb-3">{t('This section is already filled in from a previous session — moving on.', 'Esta sección ya está completa de una sesión anterior — continuando.', lang)}</p>
                  <button className="btn btn-primary" onClick={loadProductsStep}>{t('Continue', 'Continuar', lang)}</button>
                </>
              ) : (
                <>
                  <p className="text-muted small mb-3">
                    {t('A couple of sentences is enough — what do you make, and where. We’ll turn this into a proper "Scope and Facility Description" section.',
                       'Un par de oraciones es suficiente — qué produces y dónde. Convertiremos esto en una sección adecuada de "Alcance y Descripción de la Instalación".', lang)}
                  </p>
                  <textarea
                    className="form-control mb-3" rows={4}
                    placeholder={t('e.g. We produce frozen pizza and flatbread at a single facility in Miami, FL, distributed frozen to retail and foodservice customers nationwide.', 'ej. Producimos pizza congelada y pan plano en una sola instalación en Miami, FL, distribuido congelado a clientes minoristas y de servicio de alimentos a nivel nacional.', lang)}
                    value={facilityNotes}
                    onChange={e => setFacilityNotes(e.target.value)}
                    autoFocus
                  />
                  <button className="btn btn-primary" onClick={handleGenerateFacility} disabled={facilitySaving}>
                    {facilitySaving ? <><span className="spinner-border spinner-border-sm me-2" />{t('Generating…', 'Generando…', lang)}</> : `✨ ${t('Generate & Continue', 'Generar y Continuar', lang)}`}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Step 3: products ─────────────────────────────────────────────── */}
        {phase === 'products' && (
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-1">{t('Add your products', 'Agrega tus productos', lang)}</h6>
              <p className="text-muted small mb-3">
                {t('This is the step that matters most — every AI draft from here on is built from the ingredients, allergens, and intended use you enter here.',
                   'Este es el paso más importante — cada borrador de IA de aquí en adelante se construye a partir de los ingredientes, alérgenos y uso previsto que ingreses aquí.', lang)}
              </p>

              {assignedProductIds.size > 0 && (
                <div className="mb-3">
                  <div className="text-muted small fw-semibold mb-1">{t('Added so far:', 'Agregados hasta ahora:', lang)}</div>
                  <div className="d-flex flex-wrap gap-1">
                    {[...assignedProductIds].map(id => {
                      const p = allProducts.find(x => x.id === id);
                      return <span key={id} className="badge bg-success">{p?.name || `#${id}`}</span>;
                    })}
                  </div>
                </div>
              )}

              {!newProduct ? (
                <>
                  <label className="form-label small fw-semibold">{t('Pick a product from your catalog', 'Elige un producto de tu catálogo', lang)}</label>
                  <select className="form-select mb-3" onChange={e => e.target.value && startAddProduct(e.target.value)} value="">
                    <option value="">{t('— select a product —', '— selecciona un producto —', lang)}</option>
                    {allProducts.filter(p => !assignedProductIds.has(p.id)).map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary btn-sm" disabled={assignedProductIds.size === 0} onClick={handleProductsDone}>
                      {t('Done adding products →', 'Terminar de agregar productos →', lang)}
                    </button>
                  </div>
                </>
              ) : (
                <div className="border rounded p-3" style={{ background: '#f8f9fa' }}>
                  <div className="fw-semibold mb-2">{newProduct.name}</div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">{t('Intended use', 'Uso previsto', lang)}</label>
                    <input className="form-control form-control-sm" value={newProduct.intendedUse}
                      onChange={e => setNewProduct(p => ({ ...p, intendedUse: e.target.value }))}
                      placeholder={t('e.g. Ready to bake, sold frozen to retail consumers', 'ej. Listo para hornear, vendido congelado a consumidores minoristas', lang)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">{t('Ingredients', 'Ingredientes', lang)} <span className="text-danger">*</span></label>
                    <textarea className="form-control form-control-sm" rows={2} value={newProduct.ingredients}
                      onChange={e => setNewProduct(p => ({ ...p, ingredients: e.target.value }))}
                      placeholder={t('Comma separated, e.g. Wheat flour, Mozzarella cheese, Tomato sauce, Salt', 'Separados por coma, ej. Harina de trigo, Queso mozzarella, Salsa de tomate, Sal', lang)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">{t('Allergens', 'Alérgenos', lang)}</label>
                    <input className="form-control form-control-sm" value={newProduct.allergens}
                      onChange={e => setNewProduct(p => ({ ...p, allergens: e.target.value }))}
                      placeholder={t('e.g. Wheat, Milk', 'ej. Trigo, Leche', lang)} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small mb-1">{t('Storage conditions', 'Condiciones de almacenamiento', lang)}</label>
                    <input className="form-control form-control-sm" value={newProduct.storageConds}
                      onChange={e => setNewProduct(p => ({ ...p, storageConds: e.target.value }))}
                      placeholder={t('e.g. Frozen at -18°C', 'ej. Congelado a -18°C', lang)} />
                  </div>

                  <button className="btn btn-outline-secondary btn-sm mb-3" onClick={handleCheckRules} disabled={checkingRules}>
                    {checkingRules ? <span className="spinner-border spinner-border-sm" /> : `🔍 ${t('Check applicable rules', 'Verificar reglas aplicables', lang)}`}
                  </button>
                  {ruleResults?.length > 0 && (
                    <div className="mb-3">
                      {ruleResults.map((r, i) => (
                        <div key={i} className="small mb-1"><strong>{r.rule} — {r.title}</strong><div className="text-muted">{r.relevance}</div></div>
                      ))}
                    </div>
                  )}

                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleCancelProduct} disabled={productSaving}>
                      {t('Cancel', 'Cancelar', lang)}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveProduct} disabled={productSaving}>
                      {productSaving ? <span className="spinner-border spinner-border-sm" /> : t('Save Product', 'Guardar Producto', lang)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Step 4: section walkthrough ──────────────────────────────────── */}
        {phase === 'sections' && (
          sectionsToWalk.length === 0 ? (
            <div className="card shadow-sm"><div className="card-body p-4 text-center">
              <p className="mb-3">{t('Every section for this plan is already complete.', 'Todas las secciones de este plan ya están completas.', lang)}</p>
              <button className="btn btn-primary" onClick={handleAllSectionsAlreadyComplete}>
                {t('Continue', 'Continuar', lang)}
              </button>
            </div></div>
          ) : (
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="badge bg-secondary">{PLAN_LABEL[activePlanType]}</span>
                  <span className="text-muted small">{sectionIdx + 1} / {sectionsToWalk.length}</span>
                </div>
                <h6 className="fw-bold mb-2">{activeSection?.title}</h6>
                <p className="text-muted small mb-3">{SECTION_HELP[activeSection?.key] || ''}</p>

                {!sectionDraft && !sectionGenerating && (
                  <button className="btn btn-primary" onClick={handleGenerateSection}>
                    ✨ {t('Generate with AI', 'Generar con IA', lang)}
                  </button>
                )}
                {sectionGenerating && (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary mb-2" />
                    <div className="text-muted small">{sectionProgress}</div>
                  </div>
                )}
                {sectionDraft && !sectionGenerating && (
                  <>
                    <div className="alert alert-success py-2 small mb-2">
                      {t('Review below — edit directly if needed, then Save & Continue.', 'Revisa abajo — edita directamente si es necesario, luego Guardar y Continuar.', lang)}
                    </div>
                    {activeSection?.contentType === 'table_json' ? (
                      <textarea className="form-control mb-3" rows={10} style={{ fontFamily: 'monospace', fontSize: 11 }}
                        value={sectionDraft} onChange={e => setSectionDraft(e.target.value)} />
                    ) : (
                      <div
                        className="border rounded p-3 mb-3"
                        style={{ background: '#fff', maxHeight: 320, overflowY: 'auto' }}
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={e => setSectionDraft(e.currentTarget.innerHTML)}
                        dangerouslySetInnerHTML={{ __html: sectionDraft }}
                      />
                    )}
                  </>
                )}

                <div className="d-flex gap-2 mt-3">
                  {sectionDraft && !sectionGenerating && (
                    <>
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleGenerateSection}>
                        {t('Regenerate', 'Regenerar', lang)}
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveSection} disabled={sectionSaving}>
                        {sectionSaving ? <span className="spinner-border spinner-border-sm" /> : `✅ ${t('Save & Continue', 'Guardar y Continuar', lang)}`}
                      </button>
                    </>
                  )}
                  <button className="btn btn-link btn-sm text-muted ms-auto" onClick={handleSkipSection} disabled={sectionGenerating || sectionSaving}>
                    {t('Skip for now', 'Omitir por ahora', lang)}
                  </button>
                </div>
              </div>
            </div>
          )
        )}

        {/* ── Step 5: finish ───────────────────────────────────────────────── */}
        {phase === 'finish' && (
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-2">🎉 {t('Draft complete', 'Borrador completo', lang)}</h6>
              <p className="mb-3">
                {t('Every section below has been saved as a ', 'Cada sección a continuación se ha guardado como un ', lang)}
                <strong>{t('draft', 'borrador', lang)}</strong>
                {t(' directly into your plan — nothing is kept inside this wizard.', ' directamente en tu plan — nada se guarda dentro de este asistente.', lang)}
              </p>

              <div className="alert alert-info py-2 small mb-3">
                <div className="fw-semibold mb-1">📍 {t('Where to find it', 'Dónde encontrarlo', lang)}</div>
                {t('Food Safety Plans → Edit Plan (FDA or USDA) → pick any section from the sidebar on the left. Everything you just generated is there, section by section — or click "Review" below to go straight there.',
                   'Planes de Inocuidad → Editar Plan (FDA o USDA) → elige cualquier sección en la barra lateral izquierda. Todo lo que acabas de generar está ahí, sección por sección — o haz clic en "Revisar" abajo para ir directamente.', lang)}
              </div>

              <div className="alert alert-warning py-2 small mb-3">
                <div className="fw-semibold mb-1">⚠️ {t('Print / Share won’t show this yet', 'Imprimir / Compartir aún no mostrará esto', lang)}</div>
                {t('Those only display the last formally approved, signed version of the plan — never the live draft. A qualified person (PCQI for FDA, HACCP Coordinator for USDA) needs to review every section in the editor, then use "Approve & Sign" there. Only after that will Print and Share reflect what you just built.',
                   'Esos solo muestran la última versión formalmente aprobada y firmada del plan — nunca el borrador en vivo. Una persona calificada (PCQI para FDA, Coordinador HACCP para USDA) debe revisar cada sección en el editor y luego usar "Aprobar y Firmar" allí. Solo después de eso, Imprimir y Compartir reflejarán lo que acabas de construir.', lang)}
              </div>

              <div className="alert alert-warning py-2 small mb-3">
                {t('One more thing the wizard can’t do: your process flow diagram. Add that as an image in the full editor before this plan is ready for review.',
                   'Algo más que el asistente no puede hacer: tu diagrama de flujo de proceso. Agrégalo como imagen en el editor completo antes de que este plan esté listo para revisión.', lang)}
              </div>

              <div className="mb-3">
                {Object.entries(sectionDone).map(([key, status]) => (
                  <div key={key} className="small d-flex justify-content-between border-bottom py-1">
                    <span>{key}</span>
                    <span className={status === 'generated' ? 'text-success' : 'text-muted'}>{status === 'generated' ? '✓' : t('skipped', 'omitido', lang)}</span>
                  </div>
                ))}
              </div>
              <div className="d-flex gap-2">
                {planQueue.map(pt => (
                  <button key={pt} className="btn btn-primary btn-sm" onClick={() => navigate(`/sqf/food-safety/${pt}`)}>
                    {t('Review', 'Revisar', lang)} {PLAN_LABEL[pt]} →
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
